import { FEATURES, hasPermission } from '@goenhub/shared';
import {
  AlertTriangle,
  Atom,
  Banknote,
  BellRing,
  Bookmark,
  BookOpen,
  Box,
  Boxes,
  Briefcase,
  Calculator,
  Calendar,
  CalendarClock,
  ChevronLeft,
  ClipboardCheck,
  ClipboardList,
  Coins,
  Cpu,
  FileStack,
  FileText,
  ImagePlus,
  Inbox,
  Kanban,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  Link as LinkIcon,
  LogOut,
  Mail,
  MailPlus,
  Megaphone,
  MessagesSquare,
  Moon,
  Package,
  Pin,
  Presentation,
  Receipt,
  ScrollText,
  Send,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Sun,
  Tag,
  Trash2,
  Truck,
  UserCheck,
  UserCircle,
  UserCog,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { SeasonModeChip } from './SeasonModeChip';

import { useMyPermissions } from '@/features/permissions';
import { useAuth } from '@/hooks/useAuth';
import { useBoardAttention } from '@/hooks/useBoardAttention';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';

const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  Users,
  MessagesSquare,
  Calendar,
  CalendarClock,
  Send,
  Package,
  ClipboardCheck,
  ClipboardList,
  Sparkles,
  Mail,
  Megaphone,
  FileStack,
  Bookmark,
  Atom,
  MailPlus,
  Settings,
  ScrollText,
  Workflow,
  Layers,
  Box,
  Boxes,
  Kanban,
  Inbox,
  ImagePlus,
  FileText,
  Receipt,
  ReceiptText: Receipt, // ReceiptText が無い lucide バージョンへの fallback
  ShoppingCart,
  Truck,
  Cpu,
  BookOpen,
  AlertTriangle,
  Trash2,
  LayoutTemplate,
  Link: LinkIcon,
  // v3.3 IA 刷新で追加
  Banknote,
  BellRing,
  Briefcase,
  Calculator,
  Coins,
  ShieldCheck,
  ShoppingBag,
  Tag,
  UserCheck,
  UserCircle,
  UserCog,
  // v3.5 掲示板
  Pin,
  // 2026-10-07 個別説明 (独立メニュー)
  Presentation,
};

export function Sidebar() {
  const { user, signOutUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isOwner, permissions } = useMyPermissions();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(user?.sidebarCollapsed ?? false);
  // v3.5 掲示板: 未対応 + 投稿者側新着の合算バッジ (plan §6-3。board 専用の軽い実装)
  const boardAttention = useBoardAttention();

  // 自分のプロフィール編集画面へのパス (v3.3.2)
  // dept=sales かつ level=external (= SA) は /sa/profile、それ以外は /admin/users/:uid
  const profilePath =
    user?.dept === 'sales' && user?.level === 'external'
      ? '/sa/profile'
      : user?.uid
        ? `/admin/users/${user.uid}`
        : null;

  const canSee = (featureId: string) =>
    hasPermission({ isOwner, permissions, featureId, action: 'view' });

  return (
    <aside className={cn('sidebar', collapsed && 'collapsed')}>
      <div className="flex items-center gap-3 p-4 border-b border-[var(--border)]">
        <div className="sidebar-logo">G</div>
        {!collapsed && (
          <div>
            <div className="sidebar-brand-text">GoEnHub</div>
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
              Admin Console
            </div>
          </div>
        )}
      </div>

      {/* v3.2.23+: 季節モードチップ (SA は非表示、社内のみ操作可能) */}
      <SeasonModeChip collapsed={collapsed} />

      <nav className="flex-1 overflow-y-auto py-2">
        {FEATURES.map((group) => {
          // v3.3: Hub アイテムは「いずれかのタブに view 権限があれば表示」
          const visibleItems = group.items.filter((item) => {
            if (item.tabs && item.tabs.length > 0) {
              return item.tabs.some((t) => canSee(t.id));
            }
            return canSee(item.id);
          });
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.id}>
              {!collapsed && (
                <div className="nav-section-label">
                  {group.emoji ? `${group.emoji} ${group.label}` : group.label}
                </div>
              )}
              {visibleItems.map((item) => {
                const Icon = iconMap[item.icon] ?? LayoutDashboard;
                // 掲示板項目限定の count ピル (plan §6-3)
                // 投稿（要対応）とスレッド新着は性質が違うので 2 つに分けて出す。
                const isBoard = item.id === 'board.posts';
                const boardBadge = isBoard ? boardAttention.badgeTotal : 0;
                const threadBadge = isBoard ? boardAttention.threadNews : 0;
                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) => cn('nav-item', isActive && 'active')}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon />
                    {!collapsed && <span>{item.label}</span>}
                    {(boardBadge > 0 || threadBadge > 0) && !collapsed && (
                      <span className="ml-auto flex items-center gap-1">
                        {/* 要対応の投稿（赤） */}
                        {boardBadge > 0 && (
                          <span
                            title={`対応が必要な投稿 ${boardBadge} 件`}
                            className="min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--accent-red)] text-white text-[10px] font-semibold tabular-nums flex items-center justify-center"
                          >
                            {boardBadge > 99 ? '99+' : boardBadge}
                          </span>
                        )}
                        {/* スレッド新着（青・💬 付きで区別） */}
                        {threadBadge > 0 && (
                          <span
                            title={`スレッドの新着 ${threadBadge} 件`}
                            className="min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--accent)] text-white text-[10px] font-semibold tabular-nums flex items-center justify-center gap-0.5"
                          >
                            <MessagesSquare className="size-2.5" />
                            {threadBadge > 99 ? '99+' : threadBadge}
                          </span>
                        )}
                      </span>
                    )}
                    {(boardBadge > 0 || threadBadge > 0) && collapsed && (
                      <span
                        className={cn(
                          'absolute top-1.5 right-1.5 size-2 rounded-full',
                          boardBadge > 0 ? 'bg-[var(--accent-red)]' : 'bg-[var(--accent)]',
                        )}
                      />
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-[var(--border)] p-3 flex flex-col gap-2">
        {!collapsed && user && (
          <button
            type="button"
            onClick={() => profilePath && navigate(profilePath)}
            disabled={!profilePath}
            className="flex items-center gap-2 px-2 py-2 text-xs w-full text-left rounded-sm hover:bg-[var(--bg-hover)] transition-colors group disabled:cursor-default disabled:hover:bg-transparent"
            title="プロフィールを編集"
          >
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName ?? ''}
                className="w-6 h-6 rounded-full"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-[var(--bg-hover)] flex items-center justify-center text-[var(--text-secondary)]">
                {(user.displayName ?? user.email ?? '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="truncate text-[var(--text-primary)] font-medium">
                {user.displayName ?? user.email}
              </div>
              <div className="truncate text-[var(--text-muted)]">
                {user.isOwner ? 'owner' : `${user.dept}/${user.level}`}
              </div>
            </div>
            {profilePath && (
              <UserCog className="size-3.5 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            )}
          </button>
        )}
        {collapsed && user && profilePath && (
          <button
            type="button"
            onClick={() => navigate(profilePath)}
            className="nav-item !my-0 !mx-0 justify-center"
            title="プロフィールを編集"
          >
            <UserCog />
          </button>
        )}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleTheme}
            className="nav-item !my-0 !mx-0 flex-1 justify-center"
            title="テーマ切替"
          >
            {theme === 'dark' ? <Sun /> : <Moon />}
            {!collapsed && <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>}
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="nav-item !my-0 !mx-0 justify-center"
            title={collapsed ? '展開' : '折りたたむ'}
          >
            <ChevronLeft
              style={{
                transform: collapsed ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.25s',
              }}
            />
          </button>
          <button
            type="button"
            onClick={() => void signOutUser()}
            className="nav-item !my-0 !mx-0 justify-center"
            title="サインアウト"
          >
            <LogOut />
          </button>
        </div>
      </div>
    </aside>
  );
}
